<template>
  <StatsWidget
    :title="title"
    :description="description"
    icon="insights"
    :tone="tone"
    :loading="widget.loading"
    :error="widget.error"
    :has-data="hasData"
    :empty-message="emptyMessage"
    span-full
    @refresh="$emit('refresh')"
  >
    <template #actions>
      <!-- Mode toggle (year ↔ window) -->
      <div
        class="stats-forecast__mode-switch"
        role="group"
        :aria-label="$t('stats_dashboard.forecast.mode_aria')"
      >
        <button
          type="button"
          class="stats-forecast__mode-btn"
          :class="{ 'stats-forecast__mode-btn--active': requestedMode === 'year' }"
          :disabled="widget.loading && requestedMode === 'year'"
          @click="onSelectMode('year')"
        >
          {{ $t('stats_dashboard.forecast.mode.year') }}
        </button>
        <button
          type="button"
          class="stats-forecast__mode-btn"
          :class="{ 'stats-forecast__mode-btn--active': requestedMode === 'window' }"
          :disabled="widget.loading && requestedMode === 'window'"
          @click="onSelectMode('window')"
        >
          {{ $t('stats_dashboard.forecast.mode.window') }}
        </button>
      </div>

      <!-- Year switcher (year-mode only — driven by requested mode so
           the switcher hides immediately when the user toggles) -->
      <div v-if="requestedMode === 'year'" class="stats-forecast__year-switch">
        <button
          v-for="opt in yearOptions"
          :key="opt"
          type="button"
          class="stats-forecast__year-btn"
          :class="{
            'stats-forecast__year-btn--active': opt === activeYear,
          }"
          :disabled="opt === activeYear || changingYear"
          @click="onSelectYear(opt)"
        >
          {{ opt }}
        </button>
      </div>
    </template>

    <div class="stats-forecast">
      <!-- Summary strip — adapts to mode -->
      <div class="stats-forecast__summary">
        <div v-if="mode === 'year'" class="stats-forecast__summary-cell">
          <span class="stats-forecast__summary-label">
            {{ $t('stats_dashboard.forecast.annual_target') }}
          </span>
          <span class="stats-forecast__summary-value">
            {{ formatCurrency(data.annual_target_excl_vat) }}
          </span>
        </div>
        <div class="stats-forecast__summary-cell">
          <span class="stats-forecast__summary-label">
            {{ summaryLabels.sold }}
          </span>
          <span class="stats-forecast__summary-value">
            {{ formatCurrency(summaryValues.sold) }}
          </span>
        </div>
        <div class="stats-forecast__summary-cell">
          <span class="stats-forecast__summary-label">
            {{ summaryLabels.planned }}
          </span>
          <span class="stats-forecast__summary-value">
            {{ formatCurrency(summaryValues.planned) }}
          </span>
        </div>
        <div class="stats-forecast__summary-cell">
          <span class="stats-forecast__summary-label">
            {{ summaryLabels.realized }}
          </span>
          <span class="stats-forecast__summary-value">
            {{ formatCurrency(summaryValues.realized) }}
          </span>
        </div>
        <div class="stats-forecast__summary-cell">
          <span class="stats-forecast__summary-label">
            {{ summaryLabels.target }}
          </span>
          <span class="stats-forecast__summary-value">
            {{ formatCurrency(summaryValues.target) }}
          </span>
        </div>
        <div class="stats-forecast__summary-cell">
          <span class="stats-forecast__summary-label">
            {{ summaryLabels.gap }}
          </span>
          <span
            class="stats-forecast__summary-value"
            :class="{
              'stats-forecast__summary-value--positive': summaryValues.gap >= 0,
              'stats-forecast__summary-value--negative': summaryValues.gap < 0,
            }"
          >
            {{ formatGapDelta(summaryValues.gap) }}
          </span>
        </div>
        <div class="stats-forecast__summary-cell">
          <span class="stats-forecast__summary-label">
            {{ $t('stats_dashboard.forecast.pace') }}
          </span>
          <span class="stats-forecast__summary-value">
            <span class="stats-forecast__status" :class="`stats-forecast__status--${statusKey}`">
              <span class="stats-forecast__status-glyph">
                {{ statusGlyph }}
              </span>
              {{ statusLabel }}
            </span>
          </span>
        </div>
      </div>

      <!-- Line chart -->
      <div v-if="hasRows" class="stats-forecast__chart-wrap">
        <Line :data="chartData" :options="chartOptions" />
      </div>

      <!-- EOY projections — year-mode only (no EOY for an arbitrary
           window since the projection is undefined) -->
      <div v-if="mode === 'year'" class="stats-forecast__projections">
        <div class="stats-forecast__projection stats-forecast__projection--primary">
          <span class="stats-forecast__projection-label">
            {{ $t('stats_dashboard.forecast.projection_distribution') }}
          </span>
          <span class="stats-forecast__projection-value">
            {{ formatCurrency(summary.projected_eoy_distribution_aware) }}
          </span>
          <span
            class="stats-forecast__projection-delta"
            :class="{
              'stats-forecast__projection-delta--positive': eoyGap >= 0,
              'stats-forecast__projection-delta--negative': eoyGap < 0,
            }"
          >
            {{ formatGapDelta(eoyGap) }}
            <span class="stats-forecast__projection-delta-suffix">
              {{
                eoyGap >= 0
                  ? $t('stats_dashboard.forecast.above_target')
                  : $t('stats_dashboard.forecast.below_target')
              }}
            </span>
          </span>
        </div>
        <div class="stats-forecast__projection">
          <span class="stats-forecast__projection-label">
            {{ $t('stats_dashboard.forecast.projection_linear') }}
            <span class="stats-forecast__projection-tag">linear</span>
          </span>
          <span class="stats-forecast__projection-value stats-forecast__projection-value--muted">
            {{ formatCurrency(summary.projected_eoy_linear) }}
          </span>
        </div>
      </div>

      <!-- Pattern note -->
      <div v-if="patternLabel" class="stats-forecast__pattern">
        <BaseIcon name="info" size="13" />
        <span>
          {{ $t('stats_dashboard.forecast.pattern_used') }}:
          <strong>{{ patternLabel }}</strong>
          <span
            v-if="mode === 'window' && windowIntervalLabel"
            class="stats-forecast__pattern-meta"
          >
            · {{ windowIntervalLabel }}
          </span>
        </span>
      </div>
    </div>
  </StatsWidget>
</template>

<script>
  /**
   * Dashboard widget — "Prognose vs realisatie".
   *
   * Two modes:
   *   - 'year'   → Season-aware cumulative target curve + cumulative
   *                sold curve. Future months render as a dashed
   *                continuation of the target line; the sold line
   *                stops at the current month (no extrapolation).
   *                Includes EOY projections.
   *   - 'window' → Follows the dashboard's main `period` filter.
   *                Renders bucket-level cumulative curves (daily /
   *                weekly / monthly, auto-chosen by the backend).
   *                Window summary (window_target/sold/gap/pace) —
   *                no EOY projections.
   *
   * The widget reads from `dashboardStatsStore.widgets.forecast` and
   * the store's `forecastMode` / `forecastYear`. The mode toggle and
   * year switcher live in the `actions` slot.
   */
  import { computed, getCurrentInstance } from 'vue';
  import { Line } from 'vue-chartjs';
  import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
  } from 'chart.js';
  import { useDashboardStatsStore } from '@/store/dashboardStatsStore';
  import { useRevenueTargetStore } from '@/store/revenueTargetStore';
  import { useRouter } from 'vue-router';
  import StatsWidget from './StatsWidget.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import {
    formatCurrency,
    formatCurrencyCompact,
    formatCurrencyPrecise,
    formatPercent,
  } from '@/utils/statsFormat';

  ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

  // Status palette. Picked to match the dashboard's standard tone
  // vocabulary so the rest of the page stays visually consistent.
  const STATUS_META = {
    ahead: { tone: 'green', glyph: '▲' },
    on_track: { tone: 'slate', glyph: '●' },
    behind: { tone: 'rose', glyph: '▼' },
    achieved: { tone: 'green', glyph: '✓' },
    missed: { tone: 'slate', glyph: '✗' },
    no_target: { tone: 'slate', glyph: '○' },
  };

  export default {
    name: 'StatsForecast',

    components: { StatsWidget, BaseIcon, Line },

    props: {
      widget: { type: Object, required: true },
    },

    emits: ['refresh'],

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const dashboardStore = useDashboardStatsStore();
      const targetStore = useRevenueTargetStore();
      const router = useRouter();

      const data = computed(() => props.widget.data || {});
      const summary = computed(() => data.value.summary || {});

      // ============================================================
      // Mode-aware data plumbing
      //
      // Two mode refs:
      //   - `requestedMode` follows the store immediately so the toggle
      //     button gives instant feedback on click.
      //   - `mode` follows the actual response shape (`data.mode`) so
      //     the body keeps rendering the *correct* data until the new
      //     fetch lands. Without this split we'd flash an empty
      //     window/year body during the in-flight gap.
      //
      // A missing `mode` field is treated as year-mode for backwards
      // compatibility with the original Phase 3 response.
      // ============================================================

      const requestedMode = computed(() => dashboardStore.forecastMode);
      const mode = computed(() => (data.value.mode === 'window' ? 'window' : 'year'));

      // The displayed "rows" abstraction: in year-mode each row is a
      // month (`months[]`), in window-mode each row is a bucket
      // (`buckets[]`). Both share the same `cumulative_target`,
      // `cumulative_sold`, `cumulative_gap`, plus a label field.
      const rows = computed(() => {
        if (mode.value === 'window') {
          return (data.value.buckets || []).map((b) => ({
            ...b,
            label: b.bucket_label,
            // window-mode rows don't carry `is_future` — everything
            // inside the window is considered "now or past".
            is_future: false,
          }));
        }
        return (data.value.months || []).map((m) => ({
          ...m,
          label: m.month_label,
        }));
      });
      const hasRows = computed(() => rows.value.length > 0);

      const targetSet = computed(() => data.value.target_set !== false);
      const hasData = computed(() => !!props.widget.data && targetSet.value);

      const title = computed(() => {
        if (mode.value === 'window') {
          return $t('stats_dashboard.forecast.title_window');
        }
        return $t('stats_dashboard.forecast.title', {
          year: data.value.year || dashboardStore.forecastYear || '',
        });
      });

      const description = computed(() => {
        if (!targetSet.value) return '';
        if (mode.value === 'window') {
          return $t('stats_dashboard.forecast.description_window');
        }
        return $t('stats_dashboard.forecast.description');
      });

      const emptyMessage = computed(() => {
        if (!targetSet.value) {
          return $t('stats_dashboard.forecast.empty_no_target');
        }
        return $t('stats_dashboard.empty_data');
      });

      // ============================================================
      // Summary cells — labels + values pivot on mode
      // ============================================================

      const summaryLabels = computed(() => {
        if (mode.value === 'window') {
          return {
            sold: $t('stats_dashboard.forecast.window_sold'),
            planned: $t('stats_dashboard.forecast.window_planned'),
            realized: $t('stats_dashboard.forecast.window_realized'),
            target: $t('stats_dashboard.forecast.window_target'),
            gap: $t('stats_dashboard.forecast.window_gap'),
          };
        }
        return {
          sold: $t('stats_dashboard.forecast.ytd_sold'),
          planned: $t('stats_dashboard.forecast.ytd_planned'),
          realized: $t('stats_dashboard.forecast.ytd_realized'),
          target: $t('stats_dashboard.forecast.ytd_target'),
          gap: $t('stats_dashboard.forecast.ytd_gap'),
        };
      });

      const summaryValues = computed(() => {
        if (mode.value === 'window') {
          return {
            sold: summary.value.window_sold,
            planned: summary.value.window_planned,
            realized: summary.value.window_realized,
            target: summary.value.window_target,
            gap: summary.value.window_gap,
            pace: summary.value.window_pace_pct,
          };
        }
        return {
          sold: summary.value.ytd_sold,
          planned: summary.value.ytd_planned,
          realized: summary.value.ytd_realized,
          target: summary.value.ytd_target,
          gap: summary.value.ytd_gap,
          pace: summary.value.ytd_pace_pct,
        };
      });

      const windowIntervalLabel = computed(() => {
        if (mode.value !== 'window') return null;
        const interval = data.value.interval;
        if (!interval) return null;
        return $t(`stats_dashboard.forecast.interval.${interval}`);
      });

      // ============================================================
      // Status badge
      // ============================================================

      const statusKey = computed(() => {
        const s = summary.value.status;
        return STATUS_META[s] ? s : 'no_target';
      });

      const tone = computed(() => {
        return STATUS_META[statusKey.value]?.tone || 'brand';
      });

      const statusGlyph = computed(() => STATUS_META[statusKey.value]?.glyph);

      const statusLabel = computed(() => {
        const s = statusKey.value;
        if (s === 'ahead' || s === 'behind') {
          const pace = summaryValues.value.pace;
          if (pace != null) {
            // Show distance from 100% rather than the absolute value:
            // "▲ +27.5% pace" reads more naturally than "127.5%".
            const delta = pace - 100;
            const label = formatPercent(Math.abs(delta), { fractionDigits: 1 });
            return $t(`stats_dashboard.forecast.status.${s}`, {
              delta: `${delta >= 0 ? '+' : '-'}${label}`,
            });
          }
        }
        return $t(`stats_dashboard.forecast.status.${s}`);
      });

      // ============================================================
      // EOY projections
      // ============================================================

      const eoyGap = computed(() => {
        const v = summary.value.projected_eoy_gap;
        if (v != null) return Number(v);
        const proj = Number(summary.value.projected_eoy_distribution_aware) || 0;
        const target = Number(data.value.annual_target_excl_vat) || 0;
        return proj - target;
      });

      const formatGapDelta = (value) => {
        if (value == null || Number.isNaN(value)) return '—';
        const sign = value >= 0 ? '+' : '−';
        return `${sign}${formatCurrencyCompact(Math.abs(value))}`;
      };

      // ============================================================
      // Pattern label (resolution)
      // ============================================================

      const patternLabel = computed(() => {
        const res = data.value.resolution;
        if (!res) return null;
        if (res.strategy_used === 'benchmark') {
          const bm = targetStore.benchmarkByKey(res.benchmark_key);
          return bm?.name || $t('settings.forecast.strategy.benchmark.title');
        }
        if (res.strategy_used) {
          return $t(`settings.forecast.strategy.${res.strategy_used}.title`);
        }
        return null;
      });

      // ============================================================
      // Chart
      // ============================================================

      const labels = computed(() => rows.value.map((r) => r.label));

      // Target curve — always rendered for every row.
      const cumulativeTarget = computed(() =>
        rows.value.map((r) => Number(r.cumulative_target) || 0)
      );

      // Sold curve — in year-mode we render `null` for future months
      // so Chart.js stops the line at "now". Window-mode rows never
      // have `is_future`, so the sold line covers the whole window.
      const cumulativeSold = computed(() =>
        rows.value.map((r) => {
          if (r.is_future) return null;
          return Number(r.cumulative_sold) || 0;
        })
      );

      const cumulativePlanned = computed(() =>
        rows.value.map((r) => Number(r.cumulative_planned) || 0)
      );

      const cumulativeRealized = computed(() =>
        rows.value.map((r) => {
          if (r.is_future) return null;
          return Number(r.cumulative_realized) || 0;
        })
      );

      const chartData = computed(() => ({
        labels: labels.value,
        datasets: [
          {
            label: $t('stats_dashboard.forecast.legend.target'),
            data: cumulativeTarget.value,
            borderColor: '#f59e0b', // amber-500
            backgroundColor: 'rgba(245, 158, 11, 0.10)',
            borderWidth: 2,
            borderDash: [6, 4],
            tension: 0.3,
            pointRadius: 0,
            pointHoverRadius: 4,
            fill: false,
            order: 4,
          },
          {
            label: $t('stats_dashboard.forecast.legend.sold'),
            data: cumulativeSold.value,
            borderColor: '#10b981', // emerald-500
            backgroundColor: 'rgba(16, 185, 129, 0.14)',
            borderWidth: 2.5,
            tension: 0.3,
            spanGaps: false,
            pointRadius: 2,
            pointHoverRadius: 5,
            fill: 'origin',
            order: 1,
          },
          {
            label: $t('stats_dashboard.forecast.legend.planned'),
            data: cumulativePlanned.value,
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.08)',
            borderWidth: 2,
            tension: 0.3,
            pointRadius: 2,
            pointHoverRadius: 5,
            fill: false,
            order: 2,
          },
          {
            label: $t('stats_dashboard.forecast.legend.realized'),
            data: cumulativeRealized.value,
            borderColor: '#0ea5e9',
            backgroundColor: 'rgba(14, 165, 233, 0.10)',
            borderWidth: 2,
            tension: 0.3,
            spanGaps: false,
            pointRadius: 2,
            pointHoverRadius: 5,
            fill: false,
            order: 3,
          },
        ],
      }));

      const chartOptions = computed(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            align: 'end',
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              usePointStyle: true,
              pointStyle: 'circle',
              color: '#64748b',
              font: { size: 11, weight: '500' },
            },
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#fff',
            bodyColor: '#e2e8f0',
            padding: 10,
            callbacks: {
              label: (ctx) => {
                const value = ctx.raw;
                if (value == null) return null;
                return `${ctx.dataset.label}: ${formatCurrencyPrecise(value)}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: '#64748b',
              font: { size: 11 },
            },
          },
          y: {
            beginAtZero: true,
            border: { display: false },
            grid: { color: '#f1f5f9' },
            ticks: {
              color: '#64748b',
              callback: (value) => formatCurrencyCompact(value),
              font: { size: 11 },
            },
          },
        },
      }));

      // ============================================================
      // Year switcher
      // ============================================================

      const activeYear = computed(
        () => data.value.year || dashboardStore.forecastYear || new Date().getFullYear()
      );

      const yearOptions = computed(() => {
        const current = new Date().getFullYear();
        return [current + 1, current, current - 1, current - 2];
      });

      const changingYear = computed(() => props.widget.loading);

      const onSelectYear = (year) => {
        dashboardStore.setForecastYear(year);
      };

      // ============================================================
      // Mode toggle
      // ============================================================

      const onSelectMode = (nextMode) => {
        dashboardStore.setForecastMode(nextMode);
      };

      // CTA for no-target empty state. Wired up but currently
      // surfaced through `emptyMessage` only — could be hoisted into a
      // proper button via the widget's empty slot in a follow-up.
      const goToTargetSettings = () => {
        router.push('/settings/forecast');
      };

      return {
        // Data
        data,
        summary,
        hasData,
        hasRows,
        title,
        description,
        emptyMessage,
        tone,
        mode,
        requestedMode,
        summaryLabels,
        summaryValues,
        windowIntervalLabel,

        // Status
        statusKey,
        statusGlyph,
        statusLabel,

        // EOY
        eoyGap,
        formatGapDelta,

        // Pattern
        patternLabel,

        // Chart
        chartData,
        chartOptions,

        // Year switcher / mode toggle
        activeYear,
        yearOptions,
        onSelectMode,
        changingYear,
        onSelectYear,

        // CTA
        goToTargetSettings,

        // Formatters
        formatCurrency,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-forecast {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__mode-switch {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.2rem;
      background: var(--slate-100);
      border-radius: 0.5rem;
      margin-right: 0.5rem;
      @include lg-down {
        display: none;
        visibility: hidden;
      }
    }

    &__mode-btn {
      padding: 0.3rem 0.7rem;
      border: none;
      background: transparent;
      border-radius: 0.35rem;
      @include fontSize('sm');
      color: var(--slate-600);
      cursor: pointer;
      transition: all 0.15s ease;
      font-weight: 500;

      @include hover {
        color: var(--slate-700);
      }

      &--active {
        background: #fff;
        color: var(--brand-600, var(--slate-700));
        font-weight: 600;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
        cursor: default;
      }

      &:disabled {
        cursor: not-allowed;
      }
    }

    &__year-switch {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.25rem;
      background: var(--slate-100);
      border-radius: 0.5rem;
    }

    &__year-btn {
      padding: 0.3rem 0.65rem;
      border: none;
      background: transparent;
      border-radius: 0.35rem;
      @include fontSize('sm');
      color: var(--slate-600);
      cursor: pointer;
      transition: all 0.15s ease;

      @include hover {
        color: var(--slate-700);
      }

      &--active {
        background: #fff;
        color: var(--slate-700);
        font-weight: 600;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
        cursor: default;
      }

      &:disabled {
        cursor: not-allowed;
      }
    }

    &__summary {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(7.5rem, 1fr));
      gap: 0.5rem;
      padding: 0.75rem;
      background: var(--slate-50);
      border-radius: 0.625rem;
    }

    &__summary-cell {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      padding: 0.25rem 0.5rem;
    }

    &__summary-label {
      @include fontSize('sm');
      color: var(--slate-500);
      font-weight: 500;
    }

    &__summary-value {
      @include fontSize('md');
      font-weight: 600;
      color: var(--slate-700);

      &--positive {
        color: var(--emerald-600);
      }
      &--negative {
        color: var(--rose-600);
      }
    }

    &__status {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      padding: 0.2rem 0.55rem;
      border-radius: 999px;
      @include fontSize('sm');
      font-weight: 500;

      &--ahead {
        background: var(--emerald-50);
        color: var(--emerald-700);
      }
      &--on_track {
        background: var(--slate-100);
        color: var(--slate-600);
      }
      &--behind {
        background: var(--rose-50);
        color: var(--rose-700);
      }
      &--achieved {
        background: var(--emerald-50);
        color: var(--emerald-700);
      }
      &--missed {
        background: var(--slate-100);
        color: var(--slate-500);
      }
      &--no_target {
        background: var(--slate-100);
        color: var(--slate-500);
      }
    }

    &__chart-wrap {
      position: relative;
      height: 280px;
    }

    &__projections {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 0.75rem;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__projection {
      display: flex;
      flex-direction: column;
      gap: 0.3rem;
      padding: 0.85rem 1rem;
      background: var(--slate-50);
      border-radius: 0.625rem;
      border: 1px solid var(--slate-100);

      &--primary {
        background: linear-gradient(
          135deg,
          rgba(16, 185, 129, 0.05) 0%,
          rgba(59, 130, 246, 0.05) 100%
        );
        border-color: var(--brand-100, var(--slate-200));
      }
    }

    &__projection-label {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      @include fontSize('sm');
      color: var(--slate-500);
      font-weight: 500;
    }

    &__projection-tag {
      padding: 0.1rem 0.4rem;
      background: var(--slate-200);
      color: var(--slate-600);
      border-radius: 0.3rem;
      font-size: 0.65rem;
      font-weight: 600;
      letter-spacing: 0.02em;
      text-transform: lowercase;
    }

    &__projection-value {
      font-size: 1.4rem;
      font-weight: 700;
      color: var(--slate-700);

      &--muted {
        font-size: 1.1rem;
        font-weight: 600;
        color: var(--slate-500);
      }
    }

    &__projection-delta {
      @include fontSize('sm');
      font-weight: 500;

      &--positive {
        color: var(--emerald-600);
      }
      &--negative {
        color: var(--rose-600);
      }
    }

    &__projection-delta-suffix {
      color: var(--slate-500);
      font-weight: 400;
      margin-left: 0.25rem;
    }

    &__pattern {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      @include fontSize('sm');
      color: var(--slate-500);
    }

    &__pattern-meta {
      color: var(--slate-400);
    }
  }
</style>
